import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { CIO_EVENT_NAMES } from "@/lib/customerio/events";
import {
  cioDiagnostics,
  cioListLogs,
  cioRetryFailed,
  cioSendTestEvent,
  cioSyncAllCustomers,
  cioSyncCustomer,
} from "@/lib/customerio/customerio.functions";

export const Route = createFileRoute("/admin/integrations")({
  head: () => ({
    meta: [
      { title: "Integrations · Customer.io — CB16 Console" },
      { name: "description", content: "Customer.io connection status, event log, and diagnostics." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <RequirePermission perm="settings.read">
      <IntegrationsPage />
    </RequirePermission>
  ),
});

type Diag = Awaited<ReturnType<typeof cioDiagnostics>>;
type LogRow = Awaited<ReturnType<typeof cioListLogs>>[number];

const card = "rounded-sm border border-black/10 bg-white p-5";
const btn =
  "inline-flex items-center justify-center border border-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B] disabled:cursor-wait disabled:opacity-50";
const input =
  "border border-black/15 bg-white px-3 py-2 text-sm text-black focus:border-[#C6A15B] focus:outline-none";

function Pill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={
        "inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] " +
        (ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")
      }
    >
      {label}
    </span>
  );
}

function IntegrationsPage() {
  const diagFn = useServerFn(cioDiagnostics);
  const logsFn = useServerFn(cioListLogs);
  const testFn = useServerFn(cioSendTestEvent);
  const syncFn = useServerFn(cioSyncCustomer);
  const syncAllFn = useServerFn(cioSyncAllCustomers);
  const retryFn = useServerFn(cioRetryFailed);

  const [diag, setDiag] = useState<Diag | null>(null);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [eventName, setEventName] = useState("");
  const [status, setStatus] = useState("");
  const [testEmail, setTestEmail] = useState("");

  const loadLogs = useCallback(async () => {
    const rows = await logsFn({
      data: {
        search: search || undefined,
        eventName: eventName || undefined,
        status: (status || undefined) as never,
        limit: 100,
      },
    });
    setLogs(rows);
  }, [logsFn, search, eventName, status]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [d] = await Promise.all([diagFn({ data: undefined } as never), loadLogs()]);
      setDiag(d as Diag);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load diagnostics.");
    } finally {
      setLoading(false);
    }
  }, [diagFn, loadLogs]);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key);
    setMessage(null);
    setError(null);
    try {
      setMessage(await fn());
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(null);
    }
  }

  const csv = useMemo(() => {
    const head = [
      "timestamp",
      "customer",
      "email",
      "event",
      "payload_summary",
      "status",
      "http_status",
      "retry_count",
      "last_error",
    ];
    const rows = logs.map((l) =>
      [
        l.createdAt,
        l.customerId ?? "",
        l.email ?? "",
        l.eventName,
        l.payloadSummary,
        l.status,
        l.httpStatus ?? "",
        l.retryCount,
        (l.lastError ?? "").replace(/\s+/g, " "),
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(","),
    );
    return [head.join(","), ...rows].join("\n");
  }, [logs]);

  function exportCsv() {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customerio-events-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const cfg = diag?.config;
  const connected = !!cfg?.enabled && (diag?.appApi.ok ?? false);

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Customer.io — profile sync, event delivery, and diagnostics."
      />

      {loading && <p className="text-sm text-black/60">Loading diagnostics…</p>}
      {error && (
        <p role="alert" className="mb-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </p>
      )}
      {message && (
        <p className="mb-4 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {message}
        </p>
      )}

      {cfg && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className={card}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-black/70">
              Connection
            </h2>
            <dl className="space-y-2 text-sm">
              <Row label="Status">
                <Pill ok={connected} label={connected ? "Connected" : cfg.enabled ? "Degraded" : "Disabled"} />
              </Row>
              <Row label="Site ID">
                {cfg.siteIdConfigured ? (
                  <span className="font-mono text-xs tracking-tight">{cfg.siteIdMasked}</span>
                ) : (
                  <Pill ok={false} label="Missing" />
                )}
              </Row>
              <Row label="Track API key"><Pill ok={cfg.trackApiConfigured} label={cfg.trackApiConfigured ? "Configured" : "Missing"} /></Row>
              <Row label="App API key"><Pill ok={cfg.appApiConfigured} label={cfg.appApiConfigured ? "Configured" : "Missing"} /></Row>
              <Row label="Region">
                <span className="uppercase">{cfg.region}</span>{" "}
                <Pill ok={cfg.regionValid} label={cfg.regionValid ? "Valid" : "Invalid"} />
              </Row>
              <Row label="App API check">
                <Pill ok={diag!.appApi.ok} label={diag!.appApi.ok ? `HTTP ${diag!.appApi.status}` : "Unreachable"} />
              </Row>
              {cfg.missing.length > 0 && (
                <Row label="Missing env">
                  <span className="text-red-700">{cfg.missing.join(", ")}</span>
                </Row>
              )}
            </dl>
          </div>

          <div className={card}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-black/70">
              Delivery
            </h2>
            <dl className="space-y-2 text-sm">
              <Row label="Sent">{diag!.counts.sent}</Row>
              <Row label="Failed">{diag!.counts.failed}</Row>
              <Row label="Skipped">{diag!.counts.skipped}</Row>
              <Row label="Last successful event">
                {diag!.lastSuccess
                  ? `${diag!.lastSuccess.eventName} · ${new Date(diag!.lastSuccess.createdAt).toLocaleString()}`
                  : "—"}
              </Row>
              <Row label="Last failed event">
                {diag!.lastFailure
                  ? `${diag!.lastFailure.eventName} · ${new Date(diag!.lastFailure.createdAt).toLocaleString()}`
                  : "—"}
              </Row>
              <Row label="Last profile sync">
                {diag!.lastSyncAt ? new Date(diag!.lastSyncAt).toLocaleString() : "—"}
              </Row>
            </dl>
          </div>
        </div>
      )}

      <div className={card + " mt-4"}>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-black/70">Actions</h2>
        <div className="flex flex-wrap items-center gap-3">
          <input
            className={input}
            placeholder="customer@email.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            aria-label="Customer email for test and sync actions"
          />
          <button
            className={btn}
            disabled={!testEmail || busy !== null}
            onClick={() =>
              run("test", async () => {
                const r = await testFn({ data: { email: testEmail, emailFlavour: false } });
                return r.ok ? "Test event sent." : `Test event not sent: ${r.error ?? "skipped"}`;
              })
            }
          >
            Send Test Event
          </button>
          <button
            className={btn}
            disabled={!testEmail || busy !== null}
            onClick={() =>
              run("testEmail", async () => {
                const r = await testFn({ data: { email: testEmail, emailFlavour: true } });
                return r.ok ? "Test email event sent." : `Not sent: ${r.error ?? "skipped"}`;
              })
            }
          >
            Send Test Email Event
          </button>
          <button
            className={btn}
            disabled={!testEmail || busy !== null}
            onClick={() =>
              run("sync", async () => {
                const r = await syncFn({ data: { email: testEmail } });
                return r.ok ? "Customer synced." : `Sync failed: ${r.error ?? "unknown"}`;
              })
            }
          >
            Sync Customer
          </button>
          <button
            className={btn}
            disabled={busy !== null}
            onClick={() =>
              run("syncAll", async () => {
                const r = await syncAllFn({ data: undefined } as never);
                return `Synced ${r.synced}/${r.total} customers (${r.failed} failed).`;
              })
            }
          >
            Sync All Customers
          </button>
          <button
            className={btn}
            disabled={busy !== null}
            onClick={() =>
              run("retry", async () => {
                const r = await retryFn({ data: undefined } as never);
                return `Retried ${r.attempted} events, ${r.succeeded} succeeded.`;
              })
            }
          >
            Retry Failed Events
          </button>
          <button className={btn} disabled={busy !== null} onClick={() => void refresh()}>
            View Logs
          </button>
          <button className={btn} onClick={exportCsv} disabled={logs.length === 0}>
            Export Logs CSV
          </button>
        </div>
      </div>

      <div className={card + " mt-4"}>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="mr-auto text-sm font-semibold uppercase tracking-[0.18em] text-black/70">
            Last 100 Events
          </h2>
          <input
            className={input}
            placeholder="Search email / customer / event"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search events"
          />
          <select
            className={input}
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            aria-label="Filter by event"
          >
            <option value="">All events</option>
            {CIO_EVENT_NAMES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
            <option value="identify">identify</option>
          </select>
          <select
            className={input}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
          <button className={btn} onClick={() => void loadLogs()}>
            Apply
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-[12px]">
            <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/50">
              <tr>
                <th className="py-2 pr-3">Timestamp</th>
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Event</th>
                <th className="py-2 pr-3">Payload</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Retries</th>
                <th className="py-2">Last error</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-black/50">
                    No events logged yet.
                  </td>
                </tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-black/5 align-top">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {new Date(l.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">{l.email ?? l.customerId ?? "—"}</td>
                  <td className="py-2 pr-3 font-medium">{l.eventName}</td>
                  <td className="py-2 pr-3 text-black/60">{l.payloadSummary}</td>
                  <td className="py-2 pr-3">
                    <Pill ok={l.status === "sent"} label={l.status} />
                  </td>
                  <td className="py-2 pr-3">{l.retryCount}</td>
                  <td className="py-2 text-red-700">{l.lastError ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-black/5 pb-1.5">
      <dt className="text-black/55">{label}</dt>
      <dd className="text-right text-black">{children}</dd>
    </div>
  );
}